import { useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DropdownMenu, { DropdownMenuItem } from '../../../components/DropdownMenu/DropdownMenu';
import { UserContext } from '../../../context';
import axios from '../../../utils/axios';
import styles from './user-menu.module.css';
import { Icon } from '../../../components';
import { Icons } from '../../../components/Icon/Icon';

const getInitials = (name: string) => {
    const cleaned = (name || '').trim();
    if (!cleaned) return 'U';

    const parts = cleaned.split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : parts[0]?.[1] || '';
    const initials = `${first}${last}`.toUpperCase();
    return initials || 'U';
};

const UserMenu = ({ isAsideOpen }: { isAsideOpen?: boolean }) => {
    const { user, setUser } = useContext(UserContext);
    const navigate = useNavigate();

    const fullName = user?.username || user?.email || 'User';
    const initials = useMemo(() => getInitials(fullName), [fullName]);

    const logout = async () => {
        await axios.post('/auth/logout');
        setUser(null);
        localStorage.removeItem('t');
        navigate('/login');
    };

    const [isFocused, setIsFocused] = useState(false);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const menuOpenRef = useRef(false);
    const menuOpenAtMouseDownRef = useRef(false);

    if (!user) return null;

    return (
        <div className={styles.menuWrap}>
            <DropdownMenu
                className={styles.dropDown}
                dropDownMenuClassName={styles.dropDownMenu}
                menuPlacement="top"
                position={'fixed'}
                onFocus={() => {
                    setIsFocused(true);
                    menuOpenRef.current = true;
                }}
                onBlur={() => {
                    setIsFocused(false);
                    menuOpenRef.current = false;
                }}
                dropdownTrigger={
                    <button
                        ref={triggerRef}
                        type="button"
                        className={`${styles.trigger} ${isFocused && isAsideOpen ? styles.triggerFocused : ''}`}
                        aria-label="Open user menu"
                        onMouseDown={() => {
                            menuOpenAtMouseDownRef.current = menuOpenRef.current;
                        }}
                        onClick={() => {
                            const button = triggerRef.current;
                            if (!button) return;
                            if (menuOpenAtMouseDownRef.current) {
                                (document.activeElement as HTMLElement | null)?.blur();
                            } else {
                                button.focus();
                            }
                        }}
                    >
                        <span className={styles.initials}>{initials}</span>
                        <span className={styles.username}>{user.username}</span>

                        <Icon icon={Icons.chevronToggle} />
                    </button>
                }
            >
                <DropdownMenuItem icon={Icons.user} readOnly borderBottom={true}>
                    {fullName}
                </DropdownMenuItem>
                <DropdownMenuItem icon={Icons.dashboard} onClick={() => navigate('/dashboard')}>
                    User’s room
                </DropdownMenuItem>
                <DropdownMenuItem icon={Icons.disclaimer} onClick={() => navigate('/disclaimer')}>
                    Disclaimer
                </DropdownMenuItem>
                <DropdownMenuItem icon={Icons.contact} onClick={() => navigate('/contact')}>
                    Contact us
                </DropdownMenuItem>
                <DropdownMenuItem icon={Icons.questionMark} onClick={() => navigate('/contact')}>
                    Get help
                </DropdownMenuItem>
                <DropdownMenuItem icon={Icons.logout} onClick={logout} borderTop={true}>
                    Logout
                </DropdownMenuItem>
            </DropdownMenu>
        </div>
    );
};

export default UserMenu;
